import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StatistikNetomslaFaktura } from './StatistikNetomslaFaktura';
import { IndexCard } from 'app/auth/store/models';
import { firstDayThisMonth, lastDayThisMonth } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';
import { ExcelService } from '@store';


@Component({
    selector: 'app-statistik-netomsla-ui',
    templateUrl: './statistik-netomsla.component.html',
    styleUrl: './statistik-netomsla.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikNetomslaComponent implements OnInit {

    @Input() laCards: IndexCard[];
    @Input() fakturaer: StatistikNetomslaFaktura[];
    @Input() fakturaer2: StatistikNetomslaFaktura[];
    @Input() fakturaer3: StatistikNetomslaFaktura[];
    @Input() periode: { fra: string, til: string };
    @Input() periode2: { fra: string, til: string };
    @Input() periode3: { fra: string, til: string };
    @Output() periodeChange = new EventEmitter<{ fra: string, til: string }>();

    selectedYear = 1;

    constructor( private excel: ExcelService) {  }

    ngOnInit(): void {
        const newPeriode = {
            fra: firstDayThisMonth(),
            til: lastDayThisMonth()
        };
        this.onPeriodeChange(newPeriode);
    }

    onPeriodeChange(periode: { fra: string; til: string }) {
        this.periodeChange.emit(periode);
    }

    onYearSelected(year: number) {
        this.selectedYear = year;
    }

    getFakturaForPeriode(array: StatistikNetomslaFaktura[], ledelsesAnsvarligID: number): number {
        return array?.find(faktura => faktura.ledelsesAnsvarligID === ledelsesAnsvarligID)?.beloeb || 0;
    }

    getPrimoForPeriode(array: StatistikNetomslaFaktura[], ledelsesAnsvarligID: number): number {
        return array?.find(faktura => faktura.ledelsesAnsvarligID === ledelsesAnsvarligID)?.igangPrimo || 0;
    }

    getUltimoForPeriode(array: StatistikNetomslaFaktura[], ledelsesAnsvarligID: number): number {
        return array?.find(faktura => faktura.ledelsesAnsvarligID === ledelsesAnsvarligID)?.igangUltimo || 0;
    }

    private sum(array: StatistikNetomslaFaktura[] | null, property: 'beloeb' | 'igangPrimo' | 'igangUltimo'): number {
        if (!array || array.length === 0) { return 0; }
        return array.reduce((sum, item) => sum + (item[property] || 0), 0);
    }

    exportToExcel(): void {
        const exportArray: any[] = [];
        let currentArrayIndex = 1;
        let currentPeriode = this.periode;
        let currentFakturaer: StatistikNetomslaFaktura[] | null = this.fakturaer;

        for (let yearLevel = 1; yearLevel <= this.selectedYear; yearLevel++) {
            const periodLabel = [`Periode ${yearLevel}: fra ${this.formatDate(currentPeriode?.fra)} til ${this.formatDate(currentPeriode?.til)}`];
            exportArray.push(periodLabel);

            const headerRow: string[] = ['LA'];
            this.laCards.forEach(card => {
                headerRow.push(`${card.fornavn} ${card.efternavn}`);
            });
            headerRow.push('Total');
            exportArray.push(headerRow);

            const fakturaRow: (string | number)[] = ['Faktureret i perioden'];
            this.laCards.forEach(card => {
                const value = this.getFakturaForPeriode(currentFakturaer, card.cardId);
                fakturaRow.push(value);
            });
            const totalBeloeb = this.sum(currentFakturaer, 'beloeb');
            fakturaRow.push(totalBeloeb);
            exportArray.push(fakturaRow);

            const primoRow: (string | number)[] = ['Igang Primo'];
            this.laCards.forEach(card => {
                const value = this.getPrimoForPeriode(currentFakturaer, card.cardId);
                primoRow.push(value);
            });
            const totalPrimo = this.sum(currentFakturaer, 'igangPrimo');
            primoRow.push(totalPrimo);
            exportArray.push(primoRow);

            const ultimoRow: (string | number)[] = ['Igang Ultimo'];
            this.laCards.forEach(card => {
                const value = this.getUltimoForPeriode(currentFakturaer, card.cardId);
                ultimoRow.push(value);
            });
            const totalUltimo = this.sum(currentFakturaer, 'igangUltimo');
            ultimoRow.push(totalUltimo);
            exportArray.push(ultimoRow);

            const totalRow: (string | number)[] = ['Total'];
            this.laCards.forEach(card => {
                const beloeb = this.getFakturaForPeriode(currentFakturaer, card.cardId);
                const primo = this.getPrimoForPeriode(currentFakturaer, card.cardId);
                const ultimo = this.getUltimoForPeriode(currentFakturaer, card.cardId);
                const laTotal = beloeb + primo + ultimo;
                totalRow.push(laTotal);
            });
            const grandTotal = totalBeloeb + totalPrimo + totalUltimo;
            totalRow.push(grandTotal);
            exportArray.push(totalRow);

            exportArray.push(['']);

            currentArrayIndex++;
            if (currentArrayIndex === 2) {
                currentPeriode = this.periode2;
                currentFakturaer = this.fakturaer2;
            } else if (currentArrayIndex === 3) {
                currentPeriode = this.periode3;
                currentFakturaer = this.fakturaer3;
            }
        }

        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, `Netomsla_${new Date().getFullYear()}_Periode${this.selectedYear}.xlsx`);
        });
    }

    private formatDate(dateStr: string | undefined): string {
        if (!dateStr) { return ''; }
        const date = new Date(dateStr.substring(0, 10));
        if (isNaN(date.getTime())) { return ''; }
        const options: Intl.DateTimeFormatOptions = {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        };
        return date.toLocaleDateString('da', options);
    }
}
